import React, { useState } from 'react';
import { Modal, ScrollArea, Group, Text, Button, Card, Select, Input, Badge, useMantineColorScheme, useMantineTheme } from "@mantine/core";
import { IconX, IconCheck, IconInfoCircle, IconPlus, IconTrash } from "@tabler/icons-react";
import { VisiLoader, HelpTip } from "../../components/GeneralComponent/GeneralComponent";
import { useNavigate } from "react-router-dom";
import CampaignReport from "../../api/CampaignReport";
import { notifications } from '@mantine/notifications';

export function Benchmarks({ report_id, campaign_id, comparison_campaign_id, getBenchmarks }:any) {

  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const theme = useMantineTheme();
  const isDark = colorScheme === 'dark';
  const [benchmarksModalOpen, setBenchmarksModalOpen] = useState(false);
  const [isVisiLoaderVisible, setIsVisiLoaderVisible] = useState(false);
  const [goalNameList, setGoalNameList] = useState<Record<string, string>>({});
  const [benchmarks, setBenchmarks] = useState([]);
  const [localIdCounter, setLocalIdCounter] = useState(1);

  const mergedCampaignIds = [
    campaign_id,
    ...(comparison_campaign_id || [])
  ];

  const handleBenchmarksClick = (report_id, mergedCampaignIds) => {
    setBenchmarksModalOpen(true);
    setIsVisiLoaderVisible(true);
    CampaignReport.getbenchmarksdata({
      report_id: report_id,
      campaign_id: mergedCampaignIds
    })
    .then(response => {
      if (response.ok) {
        return response.json();
      } else {
        if (response.status === 401 || response.status === 400) {
          navigate("/logout");
        }
        throw new Error("API error");
      }
    })
    .then(data => {
      setIsVisiLoaderVisible(false);
      if (data.status === 1) {
        setGoalNameList(data.data.campaign_goal_result);
        const mapped = data.data.traffic_source_benchmarks.map((bm, index) => ({
          id: bm.id,
          goal: bm.goal_name,
          metric: bm.metric_name,
          redThreshold: bm.red_threshold,
          greenThreshold: bm.green_threshold,
          direction: bm.direction
        }));
        setBenchmarks(mapped);
      } else {
        notifications.show({
          color: 'red',
          icon: <IconX size="1.1rem" />,
          title: 'Error',
          message: data.message,
        });
      }
    })
    .catch(error => {
      console.error("API Error:", error);
      setIsVisiLoaderVisible(false);
    });
  };
  const handleBenchmarksCloseModal = () => setBenchmarksModalOpen(false);

  const trafficFunnelMetricsColumns = [
    { value: 'uniques', label: 'Uniques' },
    { value: 'visits', label: 'Visits' },
    { value: 'ad_spend', label: 'Ad Spend' },
    { value: 'impressions', label: 'Impressions' },
    { value: 'ctr', label: 'CTR' },
    { value: 'cpc', label: 'CPC' },
    { value: 'cpm', label: 'CPM' },
    { value: 'visiopt_conversions', label: 'Visiopt Conversions' },
    { value: 'visiopt_subscription_revenue', label: 'Visiopt Subscription Revenue' },
    { value: 'visiopt_revenue', label: 'Visiopt Revenue' },
    { value: 'profit', label: 'Profit' },
    { value: 'ucr', label: 'UCR' },
    { value: 'cr', label: 'CR' },
    { value: 'cpa', label: 'CPA' },
    { value: 'roas', label: 'ROAS' },
    { value: 'roi', label: 'ROI' },
    { value: 'aov', label: 'AOV' },
    { value: 'epc', label: 'EPC' },
    { value: 'unique_epc', label: 'Unique EPC' },
    { value: 'avg_revenue_per_unique', label: 'AVG. Revenue Per Unique' },
  ];

  const COMMON_METRICS = ["uniques", "visits", "ad_spend", "impressions", "ctr", "cpc", "cpm" ];
  
  const metricDropdown = trafficFunnelMetricsColumns.map(col => ({
    value: col.value,
    label: col.label
  }));

  const nameCount: Record<string, number> = {};
  Object.values(goalNameList).forEach(name => {
    nameCount[name] = (nameCount[name] || 0) + 1;
  });

  const combinedGoalNames: Record<string, string> = {};
  Object.entries(goalNameList).forEach(([key, name]) => {
    combinedGoalNames[name] = key; // last one wins
  });

  const goalDropdown: { value: string; label: string }[] =
  Object.entries(combinedGoalNames).map(([name, key]) => ({
    value: key,
    label: nameCount[name] > 1 ? `(Combined) ${name}` : name
  }));

  const addBenchmark = () => {
    const newId = `temp_${localIdCounter}`;
    setLocalIdCounter(localIdCounter + 1);
    setBenchmarks([
      ...benchmarks,
      {
        id: newId,
        goal: "",
        metric: "",
        direction: "higher",
        redThreshold: 0,
        greenThreshold: 0
      }
    ]);
  };

  const removeBenchmark = (id) => {
    setBenchmarks(benchmarks.filter((b) => b.id !== id));
  };

  const HIGHER_IS_BETTER = ["uniques", "visits", "impressions", "ctr", "visiopt_conversions", "visiopt_subscription_revenue", "visiopt_revenue", "profit", "ucr", "cr", "cpa", "roas", "roi", "aov", "epc", "unique_epc", "avg_revenue_per_unique" ];

  const LOWER_IS_BETTER = ["ad_spend", "cpc", "cpm" ];

  const updateBenchmark = (id, field, value) => {
    if (field === "metric") {
      const isCommon = COMMON_METRICS.includes(value);
      if (isCommon) {
        const exists = benchmarks.some(b => b.metric === value && b.id !== id);
        if (exists) {
          notifications.show({
            color: "red",
            icon: <IconX size="1.1rem" />,
            title: "Duplicate Metric",
            message: "This metric has already been selected in another benchmark.",
          });
          return;
        }
      } else {
        const currentBenchmark = benchmarks.find(b => b.id === id);
        const selectedGoal = currentBenchmark?.goal;
        if (selectedGoal) {
          const existsCombo = benchmarks.some(
            b => b.id !== id && b.metric === value && b.goal === selectedGoal
          );
          if (existsCombo) {
            notifications.show({
              color: "red",
              icon: <IconX size="1.1rem" />,
              title: "Duplicate Metric + Goal",
              message: "This metric with the same goal has already been selected in another benchmark.",
            });
            return;
          }
        }
      }
    }

    if (field === "goal") {
      const currentBenchmark = benchmarks.find(b => b.id === id);
      const metric = currentBenchmark?.metric;
      if (metric && !COMMON_METRICS.includes(metric)) {
        const existsCombo = benchmarks.some(
          b => b.id !== id && b.metric === metric && b.goal === value
        );
        if (existsCombo) {
          notifications.show({
            color: "red",
            icon: <IconX size="1.1rem" />,
            title: "Duplicate Metric + Goal",
            message: "This metric with the same goal has already been selected in another benchmark.",
          });
          return;
        }
      }
    }

    setBenchmarks(benchmarks.map(b => {
      if (b.id === id) {
        const updated = { ...b, [field]: value };

        if (field === 'metric') {
          if (COMMON_METRICS.includes(value)) {
            updated.goal = "";
          }
          const metricName = value.split("_").slice(2).join("_") || value;

          if (HIGHER_IS_BETTER.includes(metricName)) {
            updated.direction = "higher";
          } else if (LOWER_IS_BETTER.includes(metricName)) {
            updated.direction = "lower";
          } else {
            updated.direction = "higher"; // default
          }
        }

        return updated;
      }
      return b;
    }));
  };

  const handleBenchmarksSave = () => {
    for (const b of benchmarks) {
      const isCommonMetric = COMMON_METRICS.includes(b.metric);
      const goalMissing = !isCommonMetric && !b.goal;
      if (goalMissing || !b.metric || !b.redThreshold || !b.greenThreshold) {
        notifications.show({
          color: "red",
          icon: <IconX size="1.1rem" />,
          title: "Missing Fields",
          message: isCommonMetric
            ? "Please fill Metric, Red and Green values."
            : "Please fill Metric, Goal, Red and Green values.",
        });
        return;
      }
    }
    const payload = {
      report_id: report_id,
      benchmarks: benchmarks.map(b => ({
        id: b.id,
        goal_name: b.goal,
        metric_name: b.metric,
        direction: b.direction,
        redThreshold: b.redThreshold,
        greenThreshold: b.greenThreshold
      }))
    };
    setIsVisiLoaderVisible(true);
    CampaignReport.savebenchmarksdata(payload)
    .then(response => {
      if (response.ok) {
        return response.json();
      } else {
        if (response.status === 401 || response.status === 400) {
          navigate("/logout");
        }
        throw new Error("API error");
      }
    })
    .then(data => {
      setIsVisiLoaderVisible(false);
      if (data.status === 1) {
        notifications.show({
          color: "green",
          icon: <IconCheck size="1.1rem" />,
          title: "Success",
          message: "Benchmarks saved successfully!"
        });
        getBenchmarks(report_id,mergedCampaignIds);
      } else {
        notifications.show({
          color: 'red',
          icon: <IconX size="1.1rem" />,
          title: 'Error',
          message: data.message,
        });
      }
      setBenchmarksModalOpen(false);
    })
    .catch(error => {
      console.error("API Error:", error);
      setIsVisiLoaderVisible(false);
      setBenchmarksModalOpen(false);
    });
  };


  return (
    <>
      {/* ================= MODAL START ================= */}
      <Modal.Root opened={benchmarksModalOpen} onClose={handleBenchmarksCloseModal} size="75%" trapFocus={false}>
        <Modal.Overlay />
        <Modal.Content style={{ backgroundColor: isDark ? theme.colors.dark[8] : "#fff" }}>
          <VisiLoader VisiLoaderVisible={isVisiLoaderVisible} />
        <Modal.Header style={{ borderColor: isDark ? theme.colors.dark[5] : undefined }}>
            <Modal.Title style={{ color: isDark ? theme.colors.gray[0] : "inherit" }}>BENCHMARKS MODAL <HelpTip helpId={359} /></Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>
          <Modal.Body p={0} style={{ height: "70vh" }}>
            <ScrollArea style={{ height: "100%" }} px="lg" py="md">
              {benchmarks.length === 0 ? (
                <div style={{ textAlign: "center", padding: "60px 0" }}>
                  <IconInfoCircle size={48} style={{ margin: "0 auto", color: isDark ? theme.colors.gray[4] : "#999" }} />
                  <Text c="dimmed" mt="md"> No benchmarks configured yet </Text>
                  <Button leftIcon={<IconPlus size={16} />} mt="md" onClick={addBenchmark} >
                    Add Your First Benchmark
                  </Button>
                </div>
              ) : (
                <div style={{ display: "grid", gap: 20 }}>
                  {benchmarks.map((benchmark, index) => (
                    <Card 
                      withBorder 
                      p="lg" 
                      bg={isDark ? theme.colors.dark[7] : theme.colors.gray[0]} 
                      key={benchmark.id}
                      style={{
                        borderColor: isDark ? theme.colors.dark[5] : undefined,
                      }}
                    >
                      <Group position="apart" mb="md">
                        <Text fw={600}>{`Benchmark #${index + 1}`}</Text>
                        <Button variant="subtle" color="red" onClick={() => removeBenchmark(benchmark.id)}>
                          <IconTrash size={18} />
                        </Button>
                      </Group>

                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }} >
                        <div>
                          <Text size="sm" fw={500} mb={4} c={isDark ? "gray.2" : "dark"}>Metric</Text>
                          <Select
                            placeholder="Select metric..."
                            data={metricDropdown}
                            value={benchmark.metric}
                            onChange={(v) =>
                              updateBenchmark(benchmark.id, "metric", v)
                            }
                            searchable
                            withinPortal
                            styles={{
                              input: {
                                backgroundColor: isDark ? theme.colors.dark[6] : "#fff",
                                borderColor: isDark ? theme.colors.dark[5] : undefined,
                                color: isDark ? theme.colors.gray[0] : "#000",
                              },
                            }}
                          />
                        </div>
                        {!COMMON_METRICS.includes(benchmark.metric) && (
                        <div>
                          <Text size="sm" fw={500} mb={4} c={isDark ? "gray.2" : "dark"}> Goal </Text>
                          <Select
                            placeholder="Select goal..."
                            data={goalDropdown}
                            value={benchmark.goal}
                            onChange={(v) =>
                              updateBenchmark(benchmark.id, "goal", v)
                            }
                            searchable
                            withinPortal
                            styles={{
                              input: {
                                backgroundColor: isDark ? theme.colors.dark[6] : "#fff",
                                borderColor: isDark ? theme.colors.dark[5] : undefined,
                                color: isDark ? theme.colors.gray[0] : "#000",
                              },
                            }}
                          />
                        </div>
                        )}
                      </div>
                  
                      <Card 
                        withBorder 
                        p="md" 
                        radius="md"
                        bg={isDark ? theme.colors.dark[6] : "#fff"}
                        style={{
                          borderColor: isDark ? theme.colors.dark[5] : undefined,
                        }}
                      >
                        <Group position="apart" mb="sm">
                          <Text size="sm" fw={500} c={isDark ? "gray.2" : "dark"}>Thresholds</Text>

                          {benchmark.direction && (
                            <Badge color="gray" variant="light" size="xs">
                              {benchmark.direction === "lower"
                                ? "↓ Lower is better"
                                : "↑ Higher is better"}
                            </Badge>
                          )}
                        </Group>

                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                          <div>
                            <Text size="xs" fw={500} c="red" mb={4}>
                              🔴 Red (Poor) if {benchmark.direction === "lower" ? "≥" : "≤"}
                            </Text>
                            <Input
                              type="number"
                              value={benchmark.redThreshold}
                              onChange={(e) => updateBenchmark(benchmark.id, "redThreshold", e.target.value)}
                              placeholder={benchmark.direction === "lower" ? "e.g., 150" : "e.g., 2.0"}
                              styles={{
                                input: {
                                  backgroundColor: isDark ? theme.colors.dark[5] : "#fff",
                                  borderColor: isDark ? theme.colors.red[7] : "#fecaca",
                                  color: isDark ? theme.colors.gray[0] : "#000",
                                },
                              }}
                            />
                          </div>

                          <div>
                            <Text size="xs" fw={500} c="teal" mb={4}>
                              🟢 Green (Good) if {benchmark.direction === "lower" ? "≤" : "≥"}
                            </Text>
                            <Input
                              type="number"
                              value={benchmark.greenThreshold}
                              onChange={(e) => updateBenchmark(benchmark.id, "greenThreshold", e.target.value)}
                              placeholder={benchmark.direction === "lower" ? "e.g., 75" : "e.g., 5.0"}
                              styles={{
                                input: {
                                  backgroundColor: isDark ? theme.colors.dark[5] : "#fff",
                                  borderColor: isDark ? theme.colors.teal[7] : "#bbf7d0",
                                  color: isDark ? theme.colors.gray[0] : "#000",
                                },
                              }}
                            />
                          </div>
                        </div>

                        <Card 
                          mt="sm" 
                          p="xs" 
                          radius="sm"
                          style={{
                            backgroundColor: isDark ? theme.colors.dark[5] : "#fef9c3",
                            borderColor: isDark ? theme.colors.dark[4] : "#fef08a",
                            border: "1px solid",
                          }}
                        >
                          <Text size="xs" style={{ color: isDark ? theme.colors.yellow[2] : "#854d0e" }}>
                            🟡 Yellow (Watch): Values between red and green thresholds
                          </Text>
                        </Card>
                      </Card>
                    </Card>
                  ))}
      
                  <Button fullWidth variant="light" color="gray" radius="md" onClick={addBenchmark} leftIcon={<IconPlus size={18} />} >
                    Add Another Benchmark
                  </Button>
                </div>
              )}
            </ScrollArea>
          </Modal.Body>
          <Group
            position="apart"
            px="lg"
            py="md"
            style={{
              borderTop: `1px solid ${isDark ? theme.colors.dark[5] : theme.colors.gray[2]}`,
            }}
          >
            <Text size="sm" c="dimmed">
              {benchmarks.length} benchmark
              {benchmarks.length !== 1 ? "s" : ""} configured
            </Text>

            <Group>
              <Button onClick={() => handleBenchmarksSave()}>
                Save
              </Button>
            </Group>
          </Group>
        </Modal.Content>
      </Modal.Root>

      {/* ================= BUTTON TO OPEN MODAL ================= */}
      <Group position="left">
        <Button color="teal" onClick={() => handleBenchmarksClick(report_id, mergedCampaignIds)}>
          Benchmarks
        </Button>
      </Group>
    </>
  );
}